import { useState } from "react";
import { format } from "date-fns";
import { Check, Pencil, Trash2, X } from "lucide-react";
import { Avatar } from "../ui-bits";
import { AttachmentChip } from "./FilePreview";
import { cn } from "../../lib/utils";
import { Textarea } from "../ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const escapeReg = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function renderText(t, mine, users) {
  const names = users.map((u) => u.name).filter(Boolean);
  if (!names.length) return t;
  const re = new RegExp(`(@(?:${names.map(escapeReg).join("|")}))`, "g");
  return t.split(re).map((part, i) =>
    part.startsWith("@") && names.includes(part.slice(1))
      ? <span key={`mention-${i}`} className={mine ? "font-bold underline decoration-white/70 underline-offset-2" : "font-semibold rounded px-0.5 bg-[#E31E24]/15 text-[#E31E24]"}>{part}</span>
      : part
  );
}

export default function MessageBubble({ m, mine, users, onPreview, testid, canModerate = false, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const canEdit = mine && !m.is_deleted && !!onEdit;
  const canDelete = (mine || canModerate) && !m.is_deleted && !!onDelete;

  const saveEdit = () => {
    const t = draft.trim();
    if (t && t !== m.text) onEdit(m.id, t);
    setEditing(false);
  };

  return (
    <div data-testid={`${testid}-message-${m.id}`} className={cn("flex gap-2 group", mine && "flex-row-reverse")}>
      <Avatar name={m.user_name} userId={m.user_id} size="sm" className="mt-0.5" online={users.find((u) => u.id === m.user_id)?.online} />
      <div className={cn("max-w-[75%] min-w-0", mine && "flex flex-col items-end")}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold">{mine ? "You" : m.user_name}</span>
          <span className="text-[10px] text-muted-foreground">{format(new Date(m.created_at), "MMM d, HH:mm")}</span>
          {m.edited_at && !m.is_deleted && (
            <span data-testid={`${testid}-message-edited-${m.id}`} className="text-[10px] text-muted-foreground italic">(diedit)</span>
          )}
          {(canEdit || canDelete) && (
            <span className="flex items-center gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
              {canEdit && (
                <button type="button" data-testid={`${testid}-message-edit-${m.id}`} title="Edit pesan"
                  onClick={() => { setDraft(m.text || ""); setEditing(true); }}
                  className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {canDelete && (
                <button type="button" data-testid={`${testid}-message-delete-${m.id}`} title="Hapus pesan"
                  onClick={() => setConfirmOpen(true)}
                  className="p-1 rounded text-muted-foreground hover:text-[#E31E24] hover:bg-[#E31E24]/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          )}
        </div>
        {m.is_deleted ? (
          <div data-testid={`${testid}-message-deleted-${m.id}`}
            className="mt-0.5 px-3 py-1.5 rounded-lg text-sm italic text-muted-foreground border border-dashed">
            Pesan telah dihapus
          </div>
        ) : editing ? (
          <div className="mt-0.5 w-full min-w-[240px]">
            <Textarea data-testid={`${testid}-message-edit-input-${m.id}`} value={draft} rows={2} autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                if (e.key === "Escape") setEditing(false);
              }} />
            <div className="mt-1 flex gap-1 justify-end">
              <button type="button" data-testid={`${testid}-message-edit-cancel-${m.id}`} title="Batal"
                onClick={() => setEditing(false)}
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
              <button type="button" data-testid={`${testid}-message-edit-save-${m.id}`} title="Simpan"
                onClick={saveEdit} disabled={!draft.trim()}
                className="p-1 rounded text-[#1B2A6B] hover:bg-[#1B2A6B]/10 disabled:opacity-40">
                <Check className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          m.text && (
            <div className={cn("mt-0.5 px-3 py-1.5 rounded-lg text-sm whitespace-pre-wrap break-words",
              mine ? "bg-[#1B2A6B] text-white" : "bg-muted")}>
              {renderText(m.text, mine, users)}
            </div>
          )
        )}
        {!m.is_deleted && (m.attachments || []).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {m.attachments.map((a, i) => <AttachmentChip key={a.storage_path || a.id || `${m.id}-${i}`} att={a} onPreview={onPreview} />)}
          </div>
        )}
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`${testid}-message-delete-dialog-${m.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus pesan?</AlertDialogTitle>
            <AlertDialogDescription>
              Pesan akan ditampilkan sebagai &quot;Pesan telah dihapus&quot; dan lampirannya ikut dihapus. Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-message-delete-cancel-${m.id}`}>Batal</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testid}-message-delete-confirm-${m.id}`}
              className="bg-[#E31E24] hover:bg-[#c8171d]"
              onClick={() => onDelete(m.id)}>Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
